import React, { useMemo } from 'react'
import {
  Bar,
  Line,
  ComposedChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { formatCurrency } from '@/lib/format'
import { ProfitLossData } from '@/api/types'

interface ProfitLossChartProps {
  data: ProfitLossData | null
  branchId: number
  selectedYear: number
  onYearChange: (year: number) => void
  className?: string
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
]

/**
 * Profit and Loss chart with year selector
 * Shows monthly profit as bar chart with cumulative line
 */
export const ProfitLossChart: React.FC<ProfitLossChartProps> = ({
  data,
  selectedYear,
  onYearChange,
  className,
}) => {
  // Generate years for dropdown (current year and 5 years back)
  const years = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return Array.from({ length: 6 }, (_, i) => currentYear - i)
  }, [])

  const handleYearChange = (value: string) => {
    const year = parseInt(value, 10)
    onYearChange(year)
  }

  // Prepare chart data - convert month numbers to month names and calculate cumulative profit
  const chartData = useMemo(() => {
    console.log('📊 ProfitLossChart received data:', data)
    
    if (!data || !data.monthlyProfitLoss || data.monthlyProfitLoss.length === 0) {
      console.log('📊 No monthly profit/loss data available')
      return []
    }

    let cumulativeProfit = 0

    const processed = data.monthlyProfitLoss
      .sort((a, b) => a.month - b.month)
      .map((item) => {
        const profit = typeof item.profit === 'number' 
          ? item.profit 
          : (Number(item.profit) || 0)

        const safeProfit = isFinite(profit) && !isNaN(profit) ? profit : 0
        cumulativeProfit += safeProfit

        return {
          month: MONTHS[item.month - 1] || `Month ${item.month}`,
          monthNum: item.month,
          year: item.year,
          profit: safeProfit,
          cumulativeProfit: cumulativeProfit,
        }
      })
      .filter(item => {
        const isValid = item.monthNum >= 1 && item.monthNum <= 12 && 
                       !isNaN(item.profit) && isFinite(item.profit) &&
                       !isNaN(item.cumulativeProfit) && isFinite(item.cumulativeProfit)
        
        if (!isValid) {
          console.warn('📊 Filtered out invalid month data:', item)
        }
        return isValid
      })

    console.log('📊 Processed profit/loss chart data:', processed)
    return processed
  }, [data])

  // Calculate safe max value for YAxis domain
  const maxValue = useMemo(() => {
    console.log('📊 Calculating maxValue from chartData:', chartData)
    
    if (!chartData || chartData.length === 0) {
      console.log('📊 No chart data, returning default maxValue: 100')
      return 100
    }
    
    const allValues: number[] = []
    chartData.forEach((item, index) => {
      console.log(`📊 Item ${index}: profit=${item.profit}, cumulativeProfit=${item.cumulativeProfit}`)
      
      if (typeof item.profit === 'number') {
        if (!isNaN(item.profit) && isFinite(item.profit)) {
          allValues.push(Math.abs(item.profit))
          console.log(`📊 Added profit: ${Math.abs(item.profit)}`)
        }
      }
      if (typeof item.cumulativeProfit === 'number') {
        if (!isNaN(item.cumulativeProfit) && isFinite(item.cumulativeProfit)) {
          allValues.push(Math.abs(item.cumulativeProfit))
          console.log(`📊 Added cumulativeProfit: ${Math.abs(item.cumulativeProfit)}`)
        }
      }
    })
    
    console.log('📊 All valid values:', allValues)
    
    if (allValues.length === 0) {
      console.log('📊 No valid values found, returning default maxValue: 100')
      return 100
    }
    
    const max = Math.max(...allValues)
    console.log('📊 Max raw value:', max)
    
    // 20% padding add karein with minimum 100
    const paddedMax = Math.max(100, Math.ceil(max * 1.2))
    console.log('📊 Padded max value:', paddedMax)
    
    // Final validation
    if (!isFinite(paddedMax) || isNaN(paddedMax) || paddedMax <= 0) {
      console.warn('📊 Invalid padded max, returning 1000')
      return 1000
    }
    
    console.log('📊 Final maxValue:', paddedMax)
    return paddedMax
  }, [chartData])

  // Calculate safe YAxis domain with fallback
  const safeDomain = useMemo(() => {
    const safeMax = (maxValue > 0 && isFinite(maxValue) && !isNaN(maxValue)) ? maxValue : 1000
    const domain: [number, number] = [0, safeMax]
    console.log('📊 Safe domain calculated:', domain, 'maxValue:', maxValue)
    return domain
  }, [maxValue])

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="rounded-lg border bg-background p-3 shadow-sm">
          <div className="space-y-2">
            <div className="font-semibold text-sm">{label}</div>
            <div className="space-y-1 text-xs">
              {payload.map((entry: any, index: number) => (
                <div key={index} className="flex items-center justify-between gap-4">
                  <span className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded"
                      style={{ backgroundColor: entry.color }}
                    ></div>
                    {entry.name}:
                  </span>
                  <span className="font-medium">{formatCurrency(entry.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )
    }
    return null
  }

  // Format Y-axis labels
  const formatYAxisLabel = (value: number) => {
    if (typeof value !== 'number' || !isFinite(value) || isNaN(value) || value < 0) {
      console.warn('📊 Invalid YAxis value in formatter:', value)
      return '0'
    }
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}k`
    }
    return value.toString()
  }

  // Calculate total profit
  const totalProfit = data ? (typeof data.totalProfit === 'number' ? data.totalProfit : Number(data.totalProfit) || 0) : 0

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Profit and Loss</CardTitle>
            <CardDescription>
              Monthly profit and loss overview
            </CardDescription>
          </div>
          <div className="flex items-center gap-3">
            {/* Year Selector */}
            <Select value={String(selectedYear)} onValueChange={handleYearChange}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Select year" />
              </SelectTrigger>
              <SelectContent searchable={false}>
                {years.map((year) => (
                  <SelectItem key={year} value={String(year)}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!data || chartData.length === 0 || !isFinite(maxValue) || isNaN(maxValue) || maxValue <= 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No data available
          </div>
        ) : (
          <div className="space-y-4">
            {/* Chart */}
            {(() => {
              try {
                return (
                  <ResponsiveContainer width="100%" height={350}>
                    <ComposedChart
                      data={chartData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis
                        dataKey="month"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12 }}
                        className="text-muted-foreground"
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fontSize: 12 }}
                        className="text-muted-foreground"
                        tickFormatter={formatYAxisLabel}
                        domain={safeDomain}
                        allowDecimals={true}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar
                        dataKey="profit"
                        name="Profit"
                        fill="#0f172a"
                        radius={[4, 4, 0, 0]}
                      />
                      <Line
                        type="monotone"
                        dataKey="cumulativeProfit"
                        name="Cumulative Profit"
                        stroke="#64748b"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )
              } catch (error) {
                console.error('📊 ProfitLossChart rendering error:', error)
                return (
                  <div className="text-center py-8 text-red-500">
                    Chart rendering failed. Please check the data.
                  </div>
                )
              }
            })()}

            {/* Summary Total */}
            <div className="flex justify-center text-sm font-medium pt-4 border-t">
              <span>Total Profit: {formatCurrency(totalProfit)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

